import { useCallback, useEffect, useRef, useState } from 'react';
import { ErreurApi, appelApi } from './api';

/** Pendant la lecture, la position part toutes les dix secondes. */
export const INTERVALLE_VISIONNAGE_MS = 10_000;

/** `attente` : rien n'est encore parti ; `echec` : le dernier envoi n'est pas arrivé. */
export type EtatVisionnage = 'attente' | 'enregistre' | 'echec';

export type EvenementLecture = 'lecture' | 'pause' | 'fin';

export interface SuiviVisionnage {
  etat: EtatVisionnage;
  /** À brancher sur `LecteurVideo.onTemps`. */
  surTemps(secondes: number, duree: number | null): void;
  /** À brancher sur `LecteurVideo.onEtat`. */
  surEtat(evenement: EvenementLecture, secondes: number, duree: number | null): void;
}

/**
 * Enregistre la position de lecture de l'apprenant (TF-449) pour que la vidéo
 * reprenne là où il s'est arrêté, sur n'importe quel appareil : toutes les dix
 * secondes pendant la lecture, à la pause, à la fin, quand la page passe en
 * arrière-plan ou se ferme, et en quittant la leçon. Rien ne part avant le
 * premier lancement de la lecture (ouvrir une leçon n'efface pas sa position),
 * ni tant que la position n'a pas bougé d'une seconde.
 *
 * `videoId` à `null` : rien n'est suivi (aperçu, leçon sans vidéo). Un refus
 * de l'API (vidéo remplacée entre-temps, leçon fermée) arrête le suivi ; une
 * coupure réseau laisse le prochain envoi réessayer.
 *
 * L'API dit à chaque envoi si la leçon est terminée (TF-451) : la première
 * fois qu'elle l'est, `surTerminee` est appelé, pour que l'écran relise la
 * progression sans attendre.
 */
export function useEnregistrementVisionnage(
  leconId: string,
  videoId: string | null,
  surTerminee?: () => void,
): SuiviVisionnage {
  const [etat, setEtat] = useState<EtatVisionnage>('attente');
  const rappel = useRef(surTerminee);
  rappel.current = surTerminee;
  const annoncee = useRef(false);
  const position = useRef<{ secondes: number; duree: number | null } | null>(null);
  const envoyee = useRef<number | null>(null);
  const lancee = useRef(false);
  const arrete = useRef(false);
  const monte = useRef(true);

  const envoyer = useCallback(
    (enPartant = false) => {
      const lue = position.current;
      if (!videoId || !lancee.current || arrete.current || !lue) return;
      const secondes = Math.floor(lue.secondes);
      if (envoyee.current === secondes) return;
      envoyee.current = secondes;
      appelApi<{ leconTerminee: boolean }>(`/lecteur/lecons/${encodeURIComponent(leconId)}/visionnage`, {
        methode: 'PUT',
        corps: { videoId, positionSecondes: lue.secondes, dureeSecondes: lue.duree },
        keepalive: enPartant,
      }).then(
        (reponse) => {
          if (reponse?.leconTerminee && !annoncee.current) {
            annoncee.current = true;
            rappel.current?.();
          }
          if (monte.current) setEtat('enregistre');
        },
        (erreur: unknown) => {
          if (erreur instanceof ErreurApi && erreur.statut >= 400 && erreur.statut < 500) arrete.current = true;
          else if (envoyee.current === secondes) envoyee.current = null;
          if (monte.current) setEtat('echec');
        },
      );
    },
    [leconId, videoId],
  );

  useEffect(() => {
    monte.current = true;
    return () => {
      monte.current = false;
    };
  }, []);

  useEffect(() => {
    position.current = null;
    envoyee.current = null;
    lancee.current = false;
    arrete.current = false;
    annoncee.current = false;
    if (!videoId) return undefined;
    const minuterie = window.setInterval(() => envoyer(), INTERVALLE_VISIONNAGE_MS);
    const auMasquage = () => {
      if (document.visibilityState === 'hidden') envoyer(true);
    };
    const auDepart = () => envoyer(true);
    document.addEventListener('visibilitychange', auMasquage);
    window.addEventListener('pagehide', auDepart);
    return () => {
      window.clearInterval(minuterie);
      document.removeEventListener('visibilitychange', auMasquage);
      window.removeEventListener('pagehide', auDepart);
      envoyer(true);
    };
  }, [videoId, envoyer]);

  const surTemps = useCallback((secondes: number, duree: number | null) => {
    position.current = { secondes, duree };
  }, []);

  const surEtat = useCallback(
    (evenement: EvenementLecture, secondes: number, duree: number | null) => {
      position.current = { secondes: evenement === 'fin' && duree !== null ? duree : secondes, duree };
      if (evenement === 'lecture') lancee.current = true;
      else envoyer();
    },
    [envoyer],
  );

  return { etat, surTemps, surEtat };
}
